import React from 'react';
import Hero from '../components/Hero';
import ImpactStatement from '../components/ImpactStatement';
import { Award, Star, Trophy, Users, Sparkles, Film, Music, Tv, Mail, Phone } from 'lucide-react';

const HomePage = () => {
  // Harish's credentials
  const harishAwards = [
    "Femina Miss India 2023",
    "Miss Diva (2018-2021)",
    "Zee Gold Awards (2011-15)",
    "Indian Telly Awards 2014",
    "34th National Games Ceremony",
    "Doha Opening Ceremony",
    "Sa Re Ga Ma Pa Finale 2013"
  ];

  const harishFilmWork = [
    { movie: "Force 2", role: "Choreography" },
    { movie: "Commando 2", role: "Choreography" },
    { movie: "Sanam Teri Kasam", role: "Choreography" },
    { movie: "Dabangg 3", role: "Choreography" },
    { movie: "Radhe", role: "Choreography" },
    { movie: "Namastey England", role: "Montages" }
  ];

  const expertise = [
    { icon: Sparkles, text: "Large-format stage productions" },
    { icon: Users, text: "Managed audiences of 3,000–5,000+ attendees" },
    { icon: Star, text: "20+ performances in a single evening" },
    { icon: Award, text: "Grand stage design, props, costumes & choreography" }
  ];

  const experiences = [
    {
      id: 1,
      title: "Bollywood Awards Night",
      description: "A glamorous evening celebrating cinema with red carpet elegance, live performances, and star-studded entertainment that transforms your conference into an unforgettable awards ceremony.",
      image: "https://images.unsplash.com/photo-1766532776224-8ba87deee795?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHxib2xseXdvb2QlMjBzdGFnZXxlbnwwfHx8fDE3NzQwNzUyNzF8MA&ixlib=rb-4.1.0&q=85",
      highlights: ["Red Carpet Setup", "Live Performances", "Award Ceremonies", "Celebrity-style Entertainment"]
    },
    {
      id: 2,
      title: "Sufi & Serenity Night",
      description: "An enchanting spiritual journey through traditional Sufi music and mystical performances, creating an atmosphere of peace and cultural richness for your guests.",
      image: "https://images.unsplash.com/photo-1763044939313-0cb814a4e69e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxzdWZpJTIwcGVyZm9ybWFuY2V8ZW58MHx8fHwxNzc0MDc1Mjc2fDA&ixlib=rb-4.1.0&q=85",
      highlights: ["Sufi Music Ensemble", "Whirling Dervish", "Ambient Lighting", "Cultural Elegance"]
    },
    {
      id: 3,
      title: "Retro Glam Soirée",
      description: "Step back into the golden era of 70s and 80s glamour with vintage aesthetics, disco energy, and nostalgic entertainment that brings timeless elegance to modern events.",
      image: "https://images.pexels.com/photos/6982604/pexels-photo-6982604.jpeg",
      highlights: ["Vintage Décor", "Disco & Retro Music", "Period Costumes", "Classic Entertainment"]
    },
    {
      id: 4,
      title: "Talent Showcase",
      description: "A high-engagement format where participants become performers—creating connection, energy, and memorable moments through interactive talent displays.",
      image: "https://images.pexels.com/photos/2247677/pexels-photo-2247677.jpeg",
      highlights: ["Stage Spotlight", "Participant Performances", "Judges Panel", "Audience Engagement"],
      tag: "Highly Interactive"
    },
    {
      id: 5,
      title: "Cinematic Stage Experience",
      description: "A choreography-led, story-driven performance that brings cinematic storytelling to the conference stage with film-style sequences and costume transitions.",
      image: "https://images.unsplash.com/photo-1762786113000-fa587fd3d69c",
      highlights: ["Film-style Sequences", "Story-based Performances", "Costume Transitions", "Cinematic Production"],
      tag: "Our Differentiator"
    },
    {
      id: 6,
      title: "Fusion Night",
      description: "A dynamic, high-energy evening blending live music, DJ sets, and performance for a powerful closing experience with crowd engagement.",
      image: "https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg",
      highlights: ["Live DJ & Music", "Dhol & Saxophone", "Dance Floor", "High Energy Atmosphere"],
      tag: "Closing Night"
    },
    {
      id: 7,
      title: "Elegant Gala & Networking Night",
      description: "A refined, luxury evening designed for high-level networking in an elegant setting with formal seating and soft live instrumental music.",
      image: "https://images.unsplash.com/photo-1768508951126-f90917cc510e",
      highlights: ["Formal Seating", "Soft Lighting", "Live Instrumental Music", "Premium Networking"],
      tag: "Corporate Premium"
    },
    {
      id: 8,
      title: "Luxurious Private Evenings",
      description: "Curated experiences for intimate gatherings with friends and family—from game nights and beach evenings to musical nights, each crafted with precision and personalized elegance.",
      image: "https://images.unsplash.com/photo-1519167758481-83f29da8c2b6?w=800",
      highlights: ["Game Nights", "Beach Evenings", "Musical Nights", "Friends & Family Gatherings"],
      tag: "Bespoke"
    }
  ];

  return (
    <>
      <Hero />
      <ImpactStatement />

      {/* About Section */}
      <section id="about" className="py-24 px-6 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="heading-font text-5xl md:text-6xl font-bold text-white mb-4">
              About Us and Our Vision
            </h2>
            <div className="w-24 h-1 gold-bg mx-auto mb-6"></div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            <div className="bg-zinc-800 p-8 rounded-sm border border-zinc-700 hover:border-yellow-600 transition-all service-card text-center">
              <h3 className="heading-font text-4xl font-bold gold-accent mb-2">20+</h3>
              <p className="body-font text-gray-400">Years Experience</p>
            </div>
            
            <div className="bg-zinc-800 p-8 rounded-sm border border-zinc-700 hover:border-yellow-600 transition-all service-card text-center">
              <h3 className="heading-font text-4xl font-bold gold-accent mb-2">500+</h3>
              <p className="body-font text-gray-400">Events Curated</p>
            </div>
            
            <div className="bg-zinc-800 p-8 rounded-sm border border-zinc-700 hover:border-yellow-600 transition-all service-card text-center">
              <h3 className="heading-font text-4xl font-bold gold-accent mb-2">100+</h3>
              <p className="body-font text-gray-400">Corporate Clients</p>
            </div>
            
            <div className="bg-zinc-800 p-8 rounded-sm border border-zinc-700 hover:border-yellow-600 transition-all service-card text-center">
              <h3 className="heading-font text-4xl font-bold gold-accent mb-2">50+</h3>
              <p className="body-font text-gray-400">Medical Conferences</p>
            </div>
          </div>

          {/* Credentials */}
          <div className="max-w-4xl mx-auto space-y-6 mb-16">
            <div className="flex items-start gap-4">
              <Award className="gold-accent flex-shrink-0 mt-1" size={24} />
              <p className="body-font text-lg text-gray-300">
                <span className="font-semibold text-white">20+ years</span> of excellence in choreography and event curation
              </p>
            </div>
            
            <div className="flex items-start gap-4">
              <Star className="gold-accent flex-shrink-0 mt-1" size={24} />
              <p className="body-font text-lg text-gray-300">
                Collaborated with <span className="font-semibold text-white">Bollywood icons</span> including Salman Khan, Madhuri Dixit, Shah Rukh Khan, Amitabh Bachchan, Katrina Kaif and other superstars
              </p>
            </div>
            
            <div className="flex items-start gap-4">
              <Trophy className="gold-accent flex-shrink-0 mt-1" size={24} />
              <p className="body-font text-lg text-gray-300">
                Featured choreographer for <span className="font-semibold text-white">Colors, Star Plus, and Zee TV</span> with extensive work in Bollywood films, advertisements, and stage shows
              </p>
            </div>
          </div>

          {/* What We Do */}
          <div className="bg-zinc-800 border border-yellow-600 rounded-sm p-10 mb-16">
            <h3 className="heading-font text-3xl font-bold text-white mb-8 text-center">
              Our Experiences
            </h3>
            <p className="body-font text-lg text-gray-300 mb-6 text-center">
              We curate premium evenings for families, friends, and professional groups, where every element is thoughtfully designed—
              from music and ambience to food, décor, and overall flow.
            </p>
            <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              <div className="flex items-start gap-3">
                <span className="gold-accent text-xl">•</span>
                <p className="body-font text-gray-300">Curated luxury evenings for private and social gatherings</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="gold-accent text-xl">•</span>
                <p className="body-font text-gray-300">Sufi nights, ghazal evenings, and indie music experiences</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="gold-accent text-xl">•</span>
                <p className="body-font text-gray-300">Live performances with homegrown and established artists</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="gold-accent text-xl">•</span>
                <p className="body-font text-gray-300">Elegant lounge and themed social evenings</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="gold-accent text-xl">•</span>
                <p className="body-font text-gray-300">High-energy stage productions and live shows</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="gold-accent text-xl">•</span>
                <p className="body-font text-gray-300">End-to-end conference and event experiences</p>
              </div>
            </div>
          </div>

          {/* Specialization */}
          <div className="mt-16 p-8 bg-gradient-to-r from-zinc-800 to-black border border-yellow-600 rounded-sm">
            <p className="body-font text-lg text-gray-300 text-center leading-relaxed">
              <span className="gold-accent font-semibold">Specialized expertise</span> in medical conference entertainment – from complete event organization to creating extraordinary evening experiences that leave lasting impressions.
            </p>
          </div>
        </div>
      </section>

      {/* Founder's Note Section */}
      <section id="founders-note" className="py-24 px-6 bg-zinc-900">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="heading-font text-5xl md:text-6xl font-bold text-white mb-4">
              Founder's Note
            </h2>
            <div className="w-24 h-1 gold-bg mx-auto"></div>
          </div>

          {/* Founder's Message */}
          <div className="grid md:grid-cols-5 gap-12 items-start">
            {/* Founder Photo */}
            <div className="md:col-span-2 flex justify-center">
              <div className="relative">
                <div className="w-72 h-72 rounded-sm overflow-hidden border-4 border-yellow-600 bg-white">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/l27i1idi_ChatGPT%20Image%20Apr%206%2C%202026%2C%2010_40_35%20AM.png" 
                    alt="Dr. Akanksha Mantri"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="mt-6 text-center">
                  <h3 className="heading-font text-3xl text-white font-bold mb-2">
                    Dr. Akanksha Mantri
                  </h3>
                  <p className="body-font text-lg gold-accent font-semibold">
                    Founder & Director
                  </p>
                </div>
              </div>
            </div>

            {/* Message Content - REORDERED */}
            <div className="md:col-span-3 space-y-6">
              <p className="body-font text-lg text-gray-300 leading-relaxed">
                Over the years, I've been told time and again that I have a natural ability to bring people together, to create energy, and to shape moments that stay with them. That realization led me to build something of my own.
              </p>

              <div className="pt-4 pl-6 border-l-4 border-yellow-600 mb-6">
                <p className="heading-font text-2xl text-white italic leading-relaxed">
                  Elysian Evenings is an extension of that vision—<br/>
                  where every detail is thoughtfully curated, every experience is personalized, and every evening is designed to leave a lasting impression.
                </p>
              </div>

              <p className="body-font text-lg text-gray-300 leading-relaxed">
                Elysian Evenings was born from a simple but powerful belief—<br/>
                that an evening is not just about performances or production, but about <span className="text-white font-semibold">how people feel within it.</span>
              </p>

              <p className="body-font text-lg text-gray-300 leading-relaxed">
                As someone deeply connected to the world of live shows and large-scale productions, I've always been drawn to one thing—the moment when <span className="gold-accent font-semibold">an audience truly connects.</span> When a space stops feeling formal, and people begin to feel present, engaged, and part of something meaningful.
              </p>

              <p className="body-font text-lg text-gray-300 leading-relaxed">
                Being a people-centric and emotionally intuitive person, I naturally approach every event with a sense of <span className="text-white font-semibold">personal ownership.</span> I don't see it as a project—I see it as an experience I would create for myself.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Dr. Akanksha's Professional Experience */}
      <section id="founder-experience" className="py-24 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="heading-font text-5xl md:text-6xl font-bold text-white mb-4">
              Professional Experience
            </h2>
            <div className="w-24 h-1 gold-bg mx-auto mb-6"></div>
          </div>

          {/* Akanksha's Full Profile */}
          <div className="max-w-3xl mx-auto mb-16 bg-zinc-900 p-10 rounded-sm border-2 border-yellow-600">
            <div className="text-center mb-8">
              <div className="gold-bg inline-block px-4 py-1 rounded-sm mb-4">
                <span className="body-font text-xs font-bold text-black">FOUNDER & DIRECTOR</span>
              </div>
              <h3 className="heading-font text-4xl text-white font-bold mb-2">
                Dr. Akanksha Mantri
              </h3>
              <p className="body-font text-lg gold-accent font-semibold mb-3">
                Founder & Director, Elysian Evenings
              </p>
              <p className="heading-font text-base text-gray-400 italic mb-6">
                "Designing Unforgettable Conference Experiences"
              </p>
            </div>
            <div className="space-y-3 text-gray-300 body-font text-sm">
              <p>✦ Over two decades of expertise in choreography, large-scale productions, and curated event experiences</p>
              <p>✦ Worked alongside leading figures of the Indian entertainment industry including Salman Khan, Madhuri Dixit, Shah Rukh Khan, Amitabh Bachchan, Katrina Kaif, and other celebrated artists</p>
              <p>✦ Featured choreographer across premier television networks including Colors TV, Star Plus, and Zee TV</p>
              <p>✦ Core creative contributor to in-house choreography for Bigg Boss</p>
              <p>✦ Extensive experience spanning Bollywood films, television, advertisements, live stage productions, and high-impact experiential events</p>
            </div>
          </div>
        </div>
      </section>

      {/* Creative Partner - Harish */}
      <section id="creative-partner" className="py-24 px-6 bg-zinc-900">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="heading-font text-5xl md:text-6xl font-bold text-white mb-4">
              Creative Partner
            </h2>
            <div className="w-24 h-1 gold-bg mx-auto mb-6"></div>
          </div>

          {/* Harish Profile - Centered */}
          <div className="max-w-2xl mx-auto mb-16">
            <div className="bg-zinc-900 p-10 rounded-sm border-2 border-yellow-600">
              {/* Profile Photo */}
              <div className="mb-6 flex justify-center">
                <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-yellow-600 bg-white">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/tioj04fx_WhatsApp%20Image%202026-03-25%20at%2015.38.37%20%281%29.jpeg" 
                    alt="Harish Chand Bhatt"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              
              <div className="mb-6 text-center">
                <div className="gold-bg inline-block px-4 py-1 rounded-sm mb-4">
                  <span className="body-font text-xs font-bold text-black">CREATIVE PARTNER</span>
                </div>
                <h3 className="heading-font text-4xl text-white font-bold mb-2">
                  Harish Chand Bhatt
                </h3>
                <p className="body-font text-lg gold-accent font-semibold mb-3">
                  Director • Choreographer • Creative Producer
                </p>
                <div className="space-y-1 mb-6">
                  <p className="body-font text-xs text-gray-400 flex items-center justify-center gap-2">
                    <Mail size={14} className="gold-accent" /> harishbhatt21@gmail.com
                  </p>
                  <p className="body-font text-xs text-gray-400 flex items-center justify-center gap-2">
                    <Phone size={14} className="gold-accent" /> 7678465816 / 9920558702
                  </p>
                </div>
              </div>
              <div className="space-y-3 text-gray-300 body-font text-sm">
                <p>✦ Nearly two decades of experience in high-impact creative direction, live productions, and experiential storytelling</p>
                <p>✦ Founder of Shuli Media & Entertainment and Shuli Records—platforms dedicated to large-scale entertainment, content, and curated experiences</p>
                <p>✦ Creative Director for Femina Miss India 2023, shaping one of India's most prestigious beauty and cultural platforms</p>
                <p>✦ Show Director for Miss Diva (2018–2021), delivering high-production, nationally recognized showcases</p>
                <p>✦ Creative involvement in landmark events including the National Games ceremonies and the Doha Opening Ceremony</p>
                <p>✦ Directed and contributed to leading award shows such as Zee Gold Awards and Indian Telly Awards</p>
                <p>✦ Associated with some of India's most celebrated television formats including Nach Baliye, India's Got Talent, and Bigg Boss</p>
              </div>
            </div>
          </div>

          {/* Partnership Statement */}
          <div className="mb-16 bg-gradient-to-r from-zinc-900 to-black p-10 rounded-sm border border-zinc-700">
            <h3 className="heading-font text-3xl gold-accent font-bold text-center mb-6">
              A Powerful Creative Partnership
            </h3>
            <p className="body-font text-lg text-white text-center leading-relaxed mb-4">
              <span className="gold-accent font-semibold">Elysian Evenings</span> is founded by <span className="font-bold">Dr. Akanksha Mantri</span>, bringing together 15+ years of choreography excellence and Bollywood expertise.
            </p>
            <p className="body-font text-lg text-gray-300 text-center leading-relaxed">
              In strategic partnership with <span className="text-white font-bold">Harish Chand Bhatt</span>, a seasoned director with 19+ years across national ceremonies and award shows, the team delivers end-to-end execution—combining <span className="gold-accent font-semibold">cinematic storytelling with flawless production</span>.
            </p>
          </div>

          {/* Combined Scale & Expertise */}
          <div className="mb-16">
            <h3 className="heading-font text-3xl font-bold text-white mb-8 text-center">
              Combined Scale & Expertise
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {expertise.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="flex items-start gap-4 bg-zinc-900 p-6 rounded-sm border border-zinc-800">
                    <IconComponent className="gold-accent flex-shrink-0 mt-1" size={24} />
                    <p className="body-font text-gray-300">{item.text}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* OUR CREATIVE LEGACY SECTION */}
          <div className="mb-16">
            <h2 className="heading-font text-5xl font-bold text-white mb-4 text-center">
              Our Creative Legacy
            </h2>
            <div className="w-24 h-1 gold-bg mx-auto mb-6"></div>
            <p className="body-font text-lg text-gray-300 text-center max-w-4xl mx-auto mb-16 italic">
              A legacy of creating experiences across cinema, stage, and global platforms—now curated with a personal, luxury-driven approach.
            </p>

            {/* SECTION 1: CINEMATIC WORK */}
            <div className="mb-20">
              <h3 className="heading-font text-3xl font-bold gold-accent mb-8 text-center">
                Cinematic Experience
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {/* Force 2 */}
                <div className="text-center">
                  <div className="aspect-[2/3] bg-black rounded-sm overflow-hidden mb-3">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/jp4y2mom_Force%202.jpg"
                      alt="Force 2"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="heading-font text-lg text-white font-semibold">Force 2</p>
                </div>
                {/* Dabangg 3 */}
                <div className="text-center">
                  <div className="aspect-[2/3] bg-black rounded-sm overflow-hidden mb-3">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/pxekbn0o_Dabangg%203.jpg"
                      alt="Dabangg 3"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="heading-font text-lg text-white font-semibold">Dabangg 3</p>
                </div>
                {/* Radhe */}
                <div className="text-center">
                  <div className="aspect-[2/3] bg-black rounded-sm overflow-hidden mb-3">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/qi5uz2wp_Radhe.jpg"
                      alt="Radhe"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="heading-font text-lg text-white font-semibold">Radhe</p>
                </div>
                {/* Sanam Teri Kasam */}
                <div className="text-center">
                  <div className="aspect-[2/3] bg-black rounded-sm overflow-hidden mb-3">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/1tz636vy_Sanam%20teri%20kasam.jpg"
                      alt="Sanam Teri Kasam"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="heading-font text-lg text-white font-semibold">Sanam Teri Kasam</p>
                </div>
                {/* Commando 2 */}
                <div className="text-center">
                  <div className="aspect-[2/3] bg-black rounded-sm overflow-hidden mb-3">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/byx0hta7_commando%202.jpg"
                      alt="Commando 2"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="heading-font text-lg text-white font-semibold">Commando 2</p>
                </div>
                {/* Namastey England */}
                <div className="text-center">
                  <div className="aspect-[2/3] bg-black rounded-sm overflow-hidden mb-3">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/mt7k3pgp_Namastey%20England.jpg"
                      alt="Namastey England"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="heading-font text-lg text-white font-semibold">Namastey England</p>
                </div>
              </div>
            </div>

            {/* SECTION 2: LIVE & AWARD SHOWS */}
            <div className="mb-20">
              <h3 className="heading-font text-3xl font-bold gold-accent mb-4 text-center">
                Live & Large-Scale Productions
              </h3>
              <p className="body-font text-gray-400 text-center mb-8 italic">
                From televised productions to international ceremonies
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                {/* Miss Diva */}
                <div className="text-center">
                  <div className="aspect-square bg-black rounded-sm overflow-hidden mb-3">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/c8dsm1nz_Miss%20diva.webp"
                      alt="Miss Diva"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="heading-font text-base text-white font-semibold">Miss Diva</p>
                </div>
                {/* Doha Opening Ceremony */}
                <div className="text-center">
                  <div className="aspect-square bg-black rounded-sm overflow-hidden mb-3">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/6gvh59hg_Doha%20opening%20ceremopny.webp"
                      alt="Doha Opening Ceremony"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="heading-font text-base text-white font-semibold">Doha Ceremony</p>
                </div>
                {/* Femina Miss India */}
                <div className="text-center">
                  <div className="aspect-square bg-black rounded-sm overflow-hidden mb-3">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/3smjjj0x_Femina%20miss%20india.webp"
                      alt="Femina Miss India"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="heading-font text-base text-white font-semibold">Femina Miss India</p>
                </div>
                {/* Telly Awards */}
                <div className="text-center">
                  <div className="aspect-square bg-black rounded-sm overflow-hidden mb-3">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/epox17sm_Telly%20awards.png"
                      alt="Telly Awards"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="heading-font text-base text-white font-semibold">Telly Awards</p>
                </div>
                {/* WPL 2026 Opening Ceremony */}
                <div className="text-center">
                  <div className="aspect-square bg-black rounded-sm overflow-hidden mb-3">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/jqglos15_WPL-2026-Opening-Ceremony-2026-01-f39c0ba90535b7869bd4de13a28b0314.webp"
                      alt="WPL 2026 Opening Ceremony"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="heading-font text-base text-white font-semibold">WPL 2026 Ceremony</p>
                </div>
              </div>
            </div>

            {/* SECTION 3: BRAND ASSOCIATIONS */}
            <div className="mb-16">
              <h3 className="heading-font text-3xl font-bold gold-accent mb-8 text-center">
                Brands & Collaborations
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-8 max-w-5xl mx-auto">
                {/* Kalyan Jewellers */}
                <div className="bg-white p-6 rounded-sm border-2 border-zinc-800 hover:border-yellow-600 transition-all flex items-center justify-center aspect-square">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/tlfiqkhx_kalyan%20jewellers.jpg"
                    alt="Kalyan Jewellers"
                    className="w-full h-auto object-contain"
                  />
                </div>
                {/* Titan */}
                <div className="bg-white p-6 rounded-sm border-2 border-zinc-800 hover:border-yellow-600 transition-all flex items-center justify-center aspect-square">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/vcil28ci_titan.png"
                    alt="Titan"
                    className="w-full h-auto object-contain"
                  />
                </div>
                {/* HSBC */}
                <div className="bg-white p-6 rounded-sm border-2 border-zinc-800 hover:border-yellow-600 transition-all flex items-center justify-center aspect-square">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/ls51lflo_HSBC_logo_%282018%29.svg.png"
                    alt="HSBC"
                    className="w-full h-auto object-contain"
                  />
                </div>
                {/* Ponds */}
                <div className="bg-white p-6 rounded-sm border-2 border-zinc-800 hover:border-yellow-600 transition-all flex items-center justify-center aspect-square">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/7m9p2j8l_ponds.png"
                    alt="Ponds"
                    className="w-full h-auto object-contain"
                  />
                </div>
                {/* Times of India */}
                <div className="bg-white p-6 rounded-sm border-2 border-zinc-800 hover:border-yellow-600 transition-all flex items-center justify-center aspect-square">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/4s0sdd7l_the-times-of-india-logo-png_seeklogo-537021.png"
                    alt="Times of India"
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* What Sets Apart */}
          <div className="bg-gradient-to-r from-zinc-900 to-black p-10 rounded-sm border border-yellow-600">
            <h3 className="heading-font text-3xl font-bold gold-accent mb-6 text-center">
              What Sets Elysian Evenings Apart
            </h3>
            <p className="body-font text-xl text-white text-center mb-8 italic">
              "We don't just execute events—we design experiences at scale."
            </p>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="gold-bg w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="text-black" size={28} />
                </div>
                <p className="body-font text-white font-semibold">Audience Engagement</p>
              </div>
              <div className="text-center">
                <div className="gold-bg w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Film className="text-black" size={28} />
                </div>
                <p className="body-font text-white font-semibold">Visual Storytelling</p>
              </div>
              <div className="text-center">
                <div className="gold-bg w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="text-black" size={28} />
                </div>
                <p className="body-font text-white font-semibold">Seamless Execution</p>
              </div>
              <div className="text-center">
                <div className="gold-bg w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="text-black" size={28} />
                </div>
                <p className="body-font text-white font-semibold">Premium Positioning</p>
              </div>
            </div>
            <p className="body-font text-lg text-gray-300 text-center leading-relaxed mt-8">
              From Bollywood-style award nights to elegant curated evenings, each experience is crafted to leave a lasting impression.
            </p>
          </div>
        </div>
      </section>

      {/* Experiences Section */}
      <section id="experiences" className="py-24 px-6 bg-zinc-900">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="heading-font text-5xl md:text-6xl font-bold text-white mb-4">
              Signature Experiences
            </h2>
            <div className="w-24 h-1 gold-bg mx-auto mb-6"></div>
            <p className="body-font text-xl text-gray-300 max-w-3xl mx-auto">
              From Bollywood-inspired award nights to elegant cultural evenings—each experience is crafted to elevate your conference
            </p>
          </div>

          {/* Experience Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {experiences.map((exp) => (
              <div 
                key={exp.id}
                className="group relative overflow-hidden rounded-sm bg-zinc-800 border border-zinc-700 hover:border-yellow-600 transition-all service-card"
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={exp.image} 
                    alt={exp.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {exp.tag && (
                    <div className="absolute top-4 right-4 gold-bg px-3 py-1 rounded-sm">
                      <span className="body-font text-xs font-bold text-black">{exp.tag}</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="heading-font text-2xl font-bold text-white mb-3">
                    {exp.title}
                  </h3>
                  <p className="body-font text-gray-300 mb-4 leading-relaxed">
                    {exp.description}
                  </p>
                  
                  {/* Highlights */}
                  <div className="space-y-2">
                    {exp.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full gold-bg"></div>
                        <p className="body-font text-sm text-gray-400">{highlight}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
